import {
  Table,
  Thead,
  Tbody,
  Tr,
  TableContainer,
  CircularProgress,
  CircularProgressLabel,
  Th,
  Td,
} from "@chakra-ui/react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useNavigate } from "react-router-dom";
import { shortLoanAmount } from "utils/helpers";
import ContactsTableCell from "../ContactsTableCell";
import NoResults from "../NoResults";
import StatusBadge from "../StatusBadge";
import { getCustomCurrency } from "utils/uiconfighelper";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useLoansQuery } from "modules/loan/queries/use-loans-query";

type LoansTableProps = {
  small?: boolean;
  status?: any[];
  columns?: string[];
  property_id?: number;
  borrower_id?: string;
  borrower_company?: string;
  entity_id?: string;
};

const LoansTable = ({
  small,
  status,
  borrower_id,
  borrower_company,
  entity_id,
  property_id,
  columns = ["Date", "Borrower", "Name", "Type", "Status", "Target", "Amount"],
}: LoansTableProps) => {
  const { user } = useTenant();
  const navigate = useNavigate();
  const { formatDate } = useCustomFormats();
  const { data: loansData, isLoading } = useLoansQuery();

  const viewLoan = (id: string) => {
    navigate(`/loans/${id}`);
  };

  const filteredLoans =
    loansData
      ?.filter(loan => {
        if (status && !status.includes(loan.status.id)) return false;
        if (borrower_id && loan.borrower.id !== borrower_id) return false;
        if (borrower_company && loan.borrower_company.id !== borrower_company) return false;
        if (entity_id && loan.lender_company.id !== entity_id) return false;
        if (property_id && loan.property.id !== property_id) return false;
        return true;
      })
      .map(loan => {
        let target = 0;
        if (loan.status.value === "underwriting") target = 20;
        if (loan.status.value === "awaiting_approval") target = 40;
        if (loan.status.value === "escrow") target = 60;
        if (loan.status.value === "ready_to_fund") target = 80;
        if (loan.status.value === "funded") target = 100;
        return {
          ...loan,
          target,
        };
      }) || [];

  return filteredLoans.length > 0 ? (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            {columns.includes("Date") && <Th>Date</Th>}
            {columns.includes("Team") && <Th>Team</Th>}
            {user.type !== "borrower" && columns.includes("Borrower") && <Th>Borrower</Th>}
            {columns.includes("Name") && <Th>Name</Th>}
            {columns.includes("Type") && <Th>Type</Th>}
            {columns.includes("Status") && <Th>Status</Th>}
            {user.type !== "borrower" && columns.includes("Target") && <Th>Target</Th>}
            {columns.includes("Amount") && <Th isNumeric>Amount</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {filteredLoans.map(r => (
            <Tr key={r.id} onClick={() => viewLoan(r.id)} cursor="pointer">
              {columns.includes("Date") && (
                <Td>{r.funding_date ? formatDate(r.funding_date) : formatDate(r.created_at)}</Td>
              )}
              {columns.includes("Team") && (
                <Td>
                  <ContactsTableCell contacts={r.contacts} />
                </Td>
              )}
              {user.type !== "borrower" && columns.includes("Borrower") && (
                <Td>{`${r.borrower.first_name} ${r.borrower.last_name}`}</Td>
              )}
              {columns.includes("Name") && <Td>{r.name}</Td>}
              {columns.includes("Type") && <Td>{r.loan_type.title}</Td>}
              {columns.includes("Status") && (
                <Td>
                  <StatusBadge loan={r} />
                </Td>
              )}
              {user.type !== "borrower" && columns.includes("Target") && (
                <Td>
                  <CircularProgress
                    value={r.target || 0}
                    color="primary"
                    thickness="8px"
                    size="54px"
                  >
                    <CircularProgressLabel fontWeight="500" fontSize="12px">
                      {r.target || 0}%
                    </CircularProgressLabel>
                  </CircularProgress>
                </Td>
              )}
              {columns.includes("Amount") && (
                <Td isNumeric>{shortLoanAmount(r.amount, getCustomCurrency(user))}</Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  ) : (
    <NoResults loading={isLoading} message="No loans to display." />
  );
};

export default LoansTable;
